import React, { useState, useCallback } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";
import {
  PythonFrameworkId,
  PythonFrameworkMeta,
  getFrameworkMeta,
} from "./pythonAgentTemplates";
import { PythonAgentTemplateModal } from "./PythonAgentTemplateModal";

// ==========================================
// Crisp SVGs for Icons
// ==========================================

export const PythonProviderFileIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
    <polyline points="14 2 14 8 20 8" />
    <path d="m10 13-2 2 2 2" />
    <path d="m14 17 2-2-2-2" />
  </svg>
);

export const JavaScriptProviderFileIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#d97706"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
    <polyline points="14 2 14 8 20 8" />
    <path d="m10 13-2 2 2 2" />
    <path d="m14 17 2-2-2-2" />
  </svg>
);

export const GoProviderFileIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0284c7"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 22h14a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4" />
    <polyline points="14 2 14 8 20 8" />
    <path d="m10 13-2 2 2 2" />
    <path d="m14 17 2-2-2-2" />
  </svg>
);

const FormatAlignIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="18" y2="18" />
  </svg>
);

const CodeBracketsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const WifiIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export type ScriptProviderType = "python" | "javascript" | "go";

export interface ScriptTargetConfigValues {
  filePath: string;
  configJson: string;
  testGenerationInstructions: string;
  delayMs: number | string;
  extensionHook: string;
  prompts: string[];
  pythonExecutable?: string;
  env?: Record<string, string>;
  workingDir?: string;
  timeout?: number | string;
}

export type PythonTargetConfigValues = ScriptTargetConfigValues;

export const DEFAULT_PYTHON_TARGET_CONFIG: ScriptTargetConfigValues = {
  filePath: "/path/to/custom_provider.py",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_JAVASCRIPT_TARGET_CONFIG: ScriptTargetConfigValues = {
  filePath: "/path/to/custom_provider.js",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const DEFAULT_GO_TARGET_CONFIG: ScriptTargetConfigValues = {
  filePath: "/path/to/your/script",
  configJson: "{}",
  testGenerationInstructions: "",
  delayMs: 0,
  extensionHook: "",
  prompts: ["{{prompt}}"],
};

export const PYTHON_EXAMPLE_CONFIG = {
  pythonExecutable: "/usr/bin/python3",
  timeout: 30000,
};

export const JAVASCRIPT_EXAMPLE_CONFIG = {
  timeout: 30000,
  env: {
    API_KEY: "{{MY_API_KEY}}",
  },
};

export const GO_EXAMPLE_CONFIG = {
  timeout: 30000,
  args: ["--model", "gpt-4"],
};

// ==========================================
// Provider Metadata
// ==========================================

export interface ScriptProviderMeta {
  type: ScriptProviderType;
  title: string;
  fieldLabel: string;
  fieldId: string;
  fieldPlaceholder: string;
  fieldHelpPrefix: string;
  docUrl?: string;
  configDescription: string;
  examplesTitle: string;
  exampleItems: Array<{ code: string; desc: string }>;
  exampleConfig: Record<string, any>;
  icon: React.ComponentType;
  validationRequiredMessage: string;
}

export const SCRIPT_PROVIDERS: Record<ScriptProviderType, ScriptProviderMeta> = {
  python: {
    type: "python",
    title: "Python Provider",
    fieldLabel: "Python File Path",
    fieldId: "python-file-path-input",
    fieldPlaceholder: "/path/to/custom_provider.py",
    fieldHelpPrefix: "Path to your Python file that implements the provider. Optionally specify a function name after a colon.",
    configDescription: "Python executable path and timeout settings",
    examplesTitle: "Python Provider Examples",
    exampleItems: [
      { code: "./provider.py", desc: "- Uses default call_api function" },
      { code: "./provider.py:my_function", desc: "- Uses custom function name" },
      { code: "/absolute/path/to/provider.py", desc: "- Absolute path" },
    ],
    exampleConfig: PYTHON_EXAMPLE_CONFIG,
    icon: PythonProviderFileIcon,
    validationRequiredMessage: "Python file path is required",
  },
  javascript: {
    type: "javascript",
    title: "JavaScript / TypeScript Provider",
    fieldLabel: "JavaScript File Path",
    fieldId: "javascript-file-path-input",
    fieldPlaceholder: "/path/to/custom_provider.js",
    fieldHelpPrefix: "Path to your JavaScript or TypeScript file that exports a provider function. Optionally specify an export name after a colon.",
    configDescription: "Environment variables and timeout settings",
    examplesTitle: "JavaScript / TypeScript Provider Examples",
    exampleItems: [
      { code: "./provider.js", desc: "- Uses default export" },
      { code: "./provider.js:callApi", desc: "- Uses named export" },
      { code: "./provider.ts", desc: "- TypeScript file" },
    ],
    exampleConfig: JAVASCRIPT_EXAMPLE_CONFIG,
    icon: JavaScriptProviderFileIcon,
    validationRequiredMessage: "JavaScript file path is required",
  },
  go: {
    type: "go",
    title: "Go Provider",
    fieldLabel: "Go Executable Path",
    fieldId: "go-file-path-input",
    fieldPlaceholder: "/path/to/your/script",
    fieldHelpPrefix: "Path to your compiled Go binary that implements the provider interface, accepts prompts through stdin, and outputs responses to stdout.",
    configDescription: "Command-line arguments and timeout",
    examplesTitle: "Go Provider Examples",
    exampleItems: [
      { code: "./my-provider", desc: "- Compiled Go binary" },
      { code: "/usr/local/bin/my-provider", desc: "- Absolute path" },
    ],
    exampleConfig: GO_EXAMPLE_CONFIG,
    icon: GoProviderFileIcon,
    validationRequiredMessage: "Go executable path is required",
  },
};

// ==========================================
// Formatting & Validation Helpers
// ==========================================

export function formatScriptConfigJson(jsonStr: string): { formatted?: string; error?: string } {
  if (!jsonStr || !jsonStr.trim()) {
    return { formatted: "{}" };
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (err: any) {
    return { error: "Invalid JSON: " + (err?.message || "Syntax error") };
  }
}

export const formatPythonConfigJson = formatScriptConfigJson;
export const formatJavaScriptConfigJson = formatScriptConfigJson;
export const formatGoConfigJson = formatScriptConfigJson;

export function validateScriptTargetConfig(
  values: ScriptTargetConfigValues,
  providerType: ScriptProviderType = "python"
): {
  isValid: boolean;
  errors: { filePath?: string; configJson?: string };
} {
  const errors: { filePath?: string; configJson?: string } = {};
  const meta = SCRIPT_PROVIDERS[providerType] || SCRIPT_PROVIDERS.python;

  if (!values.filePath || !values.filePath.trim()) {
    errors.filePath = meta.validationRequiredMessage;
  }
  if (values.configJson && values.configJson.trim()) {
    try {
      JSON.parse(values.configJson);
    } catch (err: any) {
      errors.configJson = "Invalid JSON configuration: " + (err?.message || "Syntax error");
    }
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validatePythonTargetConfig(values: ScriptTargetConfigValues) {
  return validateScriptTargetConfig(values, "python");
}

export function validateJavaScriptTargetConfig(values: ScriptTargetConfigValues) {
  return validateScriptTargetConfig(values, "javascript");
}

export function validateGoTargetConfig(values: ScriptTargetConfigValues) {
  return validateScriptTargetConfig(values, "go");
}

// ==========================================
// Component Props
// ==========================================

export interface ScriptTargetConfigurationProps {
  providerType?: ScriptProviderType;
  frameworkId?: PythonFrameworkId;
  targetName?: string;
  values: ScriptTargetConfigValues;
  onChange: (updates: Partial<ScriptTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => void;
  isTestingConnection?: boolean;
  testResult?: {
    success: boolean;
    status?: number | null;
    message?: string | null;
    response_time?: string;
    sample_response?: string;
    sample_output?: string;
    output?: string;
    latency?: string;
    error_message?: string | null;
  } | null;
}

export type PythonTargetConfigurationProps = ScriptTargetConfigurationProps;

// ==========================================
// Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const pageHeaderTitle = css`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const providerHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const providerBadgeTitle = css`
  font-size: 1.18rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const formCardContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const formGroup = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const fieldLabel = css`
  font-size: 0.92rem;
  font-weight: 750;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 4px;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const textInput = (hasError?: boolean) => css`
  width: 100%;
  height: 42px;
  padding: 0 14px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 8px;
  outline: none;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: ${hasError ? "#ef4444" : "#0f766e"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.12)" : "rgba(15, 118, 110, 0.12)"};
  }

  &::placeholder {
    color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#475569"} !important;
  }
`;

const fieldHelpText = css`
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.45;

  a {
    color: #0f766e;
    text-decoration: underline;
    font-weight: 600;
    &:hover {
      color: #0d9488;
    }
  }

  .theme--dark & {
    color: #94a3b8 !important;
    a {
      color: #2dd4bf !important;
    }
  }
`;

const errorMessageText = css`
  margin: 0;
  font-size: 0.82rem;
  color: #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const jsonHeaderRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const formatButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-size: 0.84rem;
  font-weight: 650;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #cbd5e1 !important;
    border-color: #475569 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesBox = css`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;

  .theme--dark & {
    border-color: #334155 !important;
    background: #1e293b !important;
  }
`;

const examplesTrigger = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  background: ${isOpen ? "#f1f5f9" : "#f8fafc"};
  border: none;
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 650;
  color: #475569;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  .theme--dark & {
    background: ${isOpen ? "#334155" : "#1e293b"} !important;
    color: #cbd5e1 !important;
    &:hover {
      background: #334155 !important;
      color: #ffffff !important;
    }
  }
`;

const examplesContent = css`
  padding: 16px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 14px;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const exampleSectionTitle = css`
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 6px;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const exampleList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const exampleListItem = css`
  font-size: 0.84rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;

  code {
    background: #f1f5f9;
    color: #0f766e;
    padding: 2px 7px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.82rem;
    font-weight: 650;
    border: 1px solid #e2e8f0;
  }

  .theme--dark & {
    color: #cbd5e1 !important;
    code {
      background: #1e293b !important;
      color: #2dd4bf !important;
      border-color: #334155 !important;
    }
  }
`;

const jsonEditorContainer = (hasError?: boolean) => css`
  border: 1.5px solid ${hasError ? "#ef4444" : "#e2e8f0"};
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
  transition: border-color 0.15s ease-in-out;

  .theme--dark & {
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
    background: #0f172a !important;
  }
`;

const jsonEditorHeaderBar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  font-weight: 750;
  color: #64748b;
  letter-spacing: 0.04em;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #94a3b8 !important;
  }
`;

const jsonTextarea = css`
  width: 100%;
  min-height: 120px;
  padding: 12px 14px;
  font-family: "Geist Mono", Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 0.88rem;
  line-height: 1.5;
  color: #0f172a;
  background: #ffffff;
  border: none;
  outline: none;
  resize: vertical;

  &::placeholder {
    color: #94a3b8;
  }

  .theme--dark & {
    background: #0f172a !important;
    color: #f8fafc !important;
  }
`;

const collapsibleCard = css`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  transition: all 0.18s ease-in-out;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const collapsibleTriggerButton = (isOpen: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: ${isOpen ? "#f8fafc" : "#ffffff"};
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease-in-out;

  &:hover {
    background: #f8fafc;
  }

  .theme--dark & {
    background: ${isOpen ? "#1e293b" : "#0f172a"} !important;
    &:hover {
      background: #1e293b !important;
    }
  }
`;

const collapsibleTitle = css`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const collapsibleSubtitle = css`
  margin: 4px 0 0 0;
  font-size: 0.87rem;
  color: #64748b;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const collapsibleBody = css`
  padding: 20px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .theme--dark & {
    border-color: #334155 !important;
    background: #0f172a !important;
  }
`;

const promptsSection = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 6px;
`;

const promptsHeader = css`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const promptItemRow = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
`;

const promptTextarea = css`
  width: 100%;
  min-height: 80px;
  padding: 10px 14px;
  font-size: 0.92rem;
  font-family: inherit;
  color: #0f172a;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  resize: vertical;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: #475569 !important;
  }
`;

const trashButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  margin-top: 4px;
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  flex-shrink: 0;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
    border-color: #fecaca;
  }

  .theme--dark & {
    border-color: #334155 !important;
    color: #94a3b8 !important;
    &:hover {
      background: #450a0a !important;
      color: #f87171 !important;
      border-color: #7f1d1d !important;
    }
  }
`;

const addPromptButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 0.88rem;
  font-weight: 650;
  color: #0f766e;
  background: #f0fdfa;
  border: 1.5px solid #99f6e4;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  align-self: flex-start;

  &:hover {
    background: #ccfbf1;
    border-color: #5eead4;
  }

  .theme--dark & {
    background: #134e4a !important;
    color: #5eead4 !important;
    border-color: #0f766e !important;
    &:hover {
      background: #115e59 !important;
    }
  }
`;

const testConnectionSection = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-top: 8px;

  .theme--dark & {
    background: #0f172a !important;
    border-color: #1e293b !important;
  }
`;

const testResultSuccessPill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  color: #065f46;
  font-size: 0.84rem;
  font-weight: 600;

  .theme--dark & {
    background: #064e3b !important;
    border-color: #047857 !important;
    color: #6ee7b7 !important;
  }
`;

const testResultErrorPill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 0.84rem;
  font-weight: 600;

  .theme--dark & {
    background: #450a0a !important;
    border-color: #7f1d1d !important;
    color: #fca5a5 !important;
  }
`;

const bottomNavRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  margin-top: 8px;

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

// ==========================================
// Main Script Target Configuration Component
// ==========================================

export function ScriptTargetConfiguration({
  providerType = "python",
  frameworkId,
  targetName = "support-agent",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult,
}: ScriptTargetConfigurationProps) {
  const meta = SCRIPT_PROVIDERS[providerType] || SCRIPT_PROVIDERS.python;
  const IconComponent = meta.icon;

  const isPython = providerType === "python" || !!frameworkId;
  const framework = getFrameworkMeta(frameworkId || "python");

  // Template Modal and copy state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  const [copiedInstallCmd, setCopiedInstallCmd] = useState(false);

  // Collapsible toggle states
  const [examplesExpanded, setExamplesExpanded] = useState(false);
  const [testGenExpanded, setTestGenExpanded] = useState(false);
  const [delayExpanded, setDelayExpanded] = useState(false);
  const [extensionExpanded, setExtensionExpanded] = useState(false);

  // Validation states
  const [filePathError, setFilePathError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const activeExampleConfig = isPython ? framework.exampleConfig : meta.exampleConfig;

  // Format JSON handler
  const handleFormatJson = useCallback(() => {
    const raw = values.configJson?.trim();
    if (!raw) return;

    const res = formatScriptConfigJson(raw);
    if (res.error) {
      setJsonError(res.error);
    } else if (res.formatted) {
      onChange({ configJson: res.formatted });
      setJsonError(null);
    }
  }, [values.configJson, onChange]);

  // Load Example JSON handler
  const handleLoadExampleJson = useCallback(() => {
    onChange({ configJson: JSON.stringify(activeExampleConfig, null, 2) });
    setJsonError(null);
  }, [activeExampleConfig, onChange]);

  // Copy template handler
  const handleCopyTemplate = useCallback(() => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(framework.codeTemplate);
    } else {
      const el = document.createElement("textarea");
      el.value = framework.codeTemplate;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedTemplate(true);
    setTimeout(() => setCopiedTemplate(false), 2000);
  }, [framework.codeTemplate]);

  // Copy install command handler
  const handleCopyInstallCmd = useCallback(() => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(framework.installCommand);
    }
    setCopiedInstallCmd(true);
    setTimeout(() => setCopiedInstallCmd(false), 2000);
  }, [framework.installCommand]);

  // Download template handler
  const handleDownloadTemplate = useCallback(() => {
    const blob = new Blob([framework.codeTemplate], { type: "text/x-python;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = framework.defaultFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [framework.codeTemplate, framework.defaultFilename]);

  // Validate on Next click
  const handleNextClick = useCallback(() => {
    const { isValid, errors } = validateScriptTargetConfig(values, providerType);

    if (errors.filePath) {
      setFilePathError(errors.filePath);
    } else {
      setFilePathError(null);
    }

    if (errors.configJson) {
      setJsonError(errors.configJson);
    } else {
      setJsonError(null);
    }

    if (isValid) {
      onNext();
    }
  }, [values, providerType, onNext]);

  // Prompt item handlers
  const handlePromptChange = useCallback(
    (index: number, val: string) => {
      const updated = [...values.prompts];
      updated[index] = val;
      onChange({ prompts: updated });
    },
    [values.prompts, onChange]
  );

  const handleRemovePrompt = useCallback(
    (index: number) => {
      if (values.prompts.length > 1) {
        const updated = values.prompts.filter((_, i) => i !== index);
        onChange({ prompts: updated });
      } else {
        onChange({ prompts: [""] });
      }
    },
    [values.prompts, onChange]
  );

  const handleAddPrompt = useCallback(() => {
    onChange({ prompts: [...values.prompts, "{{prompt}}"] });
  }, [values.prompts, onChange]);

  const displayTitle = isPython ? framework.name : meta.title;

  return (
    <div css={pageWrapperStyle}>
      {/* 1. Header Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", marginBottom: "8px" }}>
        <div>
          <Heading level={1} css={pageHeaderTitle}>
            {`Configure Target: ${targetName}`}
          </Heading>
          <div css={providerHeaderRow}>
            <IconComponent />
            <span css={providerBadgeTitle}>{displayTitle}</span>
            {isPython && (
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  padding: "2px 8px",
                  background: "#ccfbf1",
                  color: "#0f766e",
                  borderRadius: "9999px",
                  border: "1px solid #5eead4",
                  textTransform: "uppercase",
                }}
              >
                {framework.badge}
              </span>
            )}
          </div>
        </div>
        {renderModeSwitcher && (
          <div style={{ flexShrink: 0, marginTop: "4px" }}>
            {renderModeSwitcher}
          </div>
        )}
      </div>

      {/* 2. Main Form Card Container */}
      <div css={formCardContainer}>
        {/* Framework Information Banner */}
        {isPython && (
          <div
            style={{
              background: "linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%)",
              border: "1px solid #99f6e4",
              borderRadius: "10px",
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "1.02rem", fontWeight: 750, color: "#0f766e" }}>
                    {framework.name}
                  </span>
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", background: "#ccfbf1", color: "#0f766e", borderRadius: "9999px", border: "1px solid #5eead4", textTransform: "uppercase" }}>
                    {framework.badge}
                  </span>
                </div>
                <p style={{ margin: "4px 0 0 0", fontSize: "0.86rem", color: "#475569" }}>
                  {framework.description}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTemplateModalOpen(true)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "7px 14px",
                  fontSize: "0.84rem",
                  fontWeight: 700,
                  background: "#0f766e",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                <CodeBracketsIcon />
                <span>Generate Template File</span>
              </button>
            </div>

            {/* Install command bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                background: "#0f172a",
                borderRadius: "6px",
                padding: "8px 12px",
                fontSize: "0.82rem",
                fontFamily: "monospace",
                color: "#38bdf8",
              }}
            >
              <span>{framework.installCommand}</span>
              <button
                type="button"
                onClick={handleCopyInstallCmd}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  background: "#1e293b",
                  color: "#e2e8f0",
                  border: "1px solid #334155",
                  borderRadius: "4px",
                  padding: "3px 8px",
                  fontSize: "0.76rem",
                  fontWeight: 650,
                  cursor: "pointer",
                }}
              >
                {copiedInstallCmd ? "Copied!" : "Copy Command"}
              </button>
            </div>
          </div>
        )}

        {/* Field 1: File / Executable Path */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor={meta.fieldId}>
            {meta.fieldLabel} <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            id={meta.fieldId}
            type="text"
            css={textInput(!!filePathError)}
            placeholder={isPython ? framework.defaultFilePath : meta.fieldPlaceholder}
            value={values.filePath}
            onChange={(e) => {
              onChange({ filePath: e.target.value });
              if (filePathError) setFilePathError(null);
            }}
          />
          {filePathError && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {filePathError}
            </p>
          )}
          {!isPython && meta.fieldHelpPrefix && (
            <p css={fieldHelpText}>{meta.fieldHelpPrefix}</p>
          )}

          {/* Quick-Start Template Row */}
          {isPython && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                padding: "10px 14px",
                background: "#f8fafc",
                border: "1px dashed #cbd5e1",
                borderRadius: "8px",
                marginTop: "4px",
              }}
            >
              <span style={{ fontSize: "0.83rem", color: "#475569" }}>
                Quick-Start Template ({framework.defaultFilename})
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  onClick={() => setTemplateModalOpen(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 10px",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    background: "#0f766e",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Generate Template
                </button>
                <button
                  type="button"
                  onClick={handleCopyTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 10px",
                    fontSize: "0.8rem",
                    fontWeight: 650,
                    background: "#ffffff",
                    color: "#334155",
                    border: "1px solid #cbd5e1",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  {copiedTemplate ? "Copied!" : "Copy"}
                </button>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "5px 10px",
                    fontSize: "0.8rem",
                    fontWeight: 650,
                    background: "#ffffff",
                    color: "#334155",
                    border: "1px solid #cbd5e1",
                    borderRadius: "5px",
                    cursor: "pointer",
                  }}
                >
                  Download .py
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Python Executable / Virtual Environment Path */}
        {isPython && (
          <div css={formGroup}>
            <label css={fieldLabel} htmlFor="python-executable-path-input">
              Python Executable / Virtual Environment Path
            </label>
            <input
              id="python-executable-path-input"
              type="text"
              css={textInput()}
              placeholder="/path/to/venv/bin/python or python3"
              value={values.pythonExecutable || ""}
              onChange={(e) => onChange({ pythonExecutable: e.target.value })}
            />
            <p css={fieldHelpText}>
              Path to Python executable or virtual environment containing installed framework dependencies (optional).
            </p>
          </div>
        )}

        {/* Field 2: Configuration (JSON) */}
        <div css={formGroup}>
          <div css={jsonHeaderRow}>
            <label css={fieldLabel} htmlFor={`${meta.fieldId}-config-json`}>
              Configuration (JSON)
            </label>
            <button
              type="button"
              css={formatButton}
              onClick={handleFormatJson}
              title="Format JSON with 2-space indentation"
            >
              <FormatAlignIcon />
              <span>Format</span>
            </button>
          </div>

          {/* Examples Collapsible */}
          <div css={examplesBox}>
            <button
              type="button"
              css={examplesTrigger(examplesExpanded)}
              onClick={() => setExamplesExpanded(!examplesExpanded)}
              aria-expanded={examplesExpanded}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CodeBracketsIcon />
                <span>Examples</span>
              </span>
              <ChevronDownIcon
                style={{
                  transform: examplesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.18s ease-in-out",
                }}
              />
            </button>

            {examplesExpanded && (
              <div css={examplesContent}>
                <div>
                  <div css={exampleSectionTitle}>{meta.examplesTitle}</div>
                  <ul css={exampleList}>
                    {meta.exampleItems.map((item, idx) => (
                      <li key={idx} css={exampleListItem}>
                        <code>{item.code}</code>
                        <span>{item.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div css={exampleSectionTitle}>JSON Config Example</div>
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "10px 12px",
                      fontSize: "0.82rem",
                      fontFamily: "monospace",
                      color: "#334155",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(meta.exampleConfig, null, 2)}
                    </pre>
                    <button
                      type="button"
                      onClick={handleLoadExampleJson}
                      style={{
                        alignSelf: "flex-start",
                        padding: "3px 8px",
                        fontSize: "0.78rem",
                        fontWeight: 650,
                        background: "#0f766e",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Use This Example
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* JSON Editor Box */}
          <div css={jsonEditorContainer(!!jsonError)}>
            <div css={jsonEditorHeaderBar}>
              <span>JSON</span>
            </div>
            <textarea
              id={`${meta.fieldId}-config-json`}
              css={jsonTextarea}
              placeholder="{}"
              value={values.configJson}
              onChange={(e) => {
                onChange({ configJson: e.target.value });
                if (jsonError) setJsonError(null);
              }}
              spellCheck={false}
            />
          </div>

          {jsonError ? (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {jsonError}
            </p>
          ) : (
            <p css={fieldHelpText}>{meta.configDescription}</p>
          )}
        </div>

        {/* Field 3: Test Generation (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(testGenExpanded)}
            onClick={() => setTestGenExpanded(!testGenExpanded)}
            aria-expanded={testGenExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Test Generation</h3>
              <p css={collapsibleSubtitle}>
                Configure how red team test cases are generated for this target
              </p>
            </div>
            <ChevronDownIcon
              style={{
                transform: testGenExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {testGenExpanded && (
            <div css={collapsibleBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="test-gen-instructions-input">
                  Instructions
                </label>
                <p css={fieldHelpText}>
                  Additional guidance for generating red team attacks. Useful if you need to tweak
                  anything from structure to specific scenarios for your application.
                </p>
                <textarea
                  id="test-gen-instructions-input"
                  css={textInput()}
                  style={{ minHeight: "80px", height: "auto", padding: "10px 14px", resize: "vertical" }}
                  placeholder="Generate the attack as a news article"
                  value={values.testGenerationInstructions}
                  onChange={(e) => onChange({ testGenerationInstructions: e.target.value })}
                />
                <details style={{ marginTop: "6px", fontSize: "0.85rem", color: "#64748b" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>View examples</summary>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "18px", lineHeight: "1.6" }}>
                    <li>Generate the attack as a news article</li>
                    <li>Generate the attack as a receipt or invoice</li>
                    <li>Limit the attack to 300 characters</li>
                    <li>Adopt a frustrated customer persona when attacking</li>
                    <li>
                      Format the attack as a JSON object: <code>{`{"message": "..."}`}</code>
                    </li>
                  </ul>
                </details>
              </div>

              <div css={formGroup}>
                <label css={fieldLabel}>Generate Multiple Variables</label>
                <p css={fieldHelpText}>
                  Define variables that will be generated alongside adversarial prompts. Use{" "}
                  <code>{"{{variable_name}}"}</code> in your request body, headers, or URL.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Field 4: Delay (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(delayExpanded)}
            onClick={() => setDelayExpanded(!delayExpanded)}
            aria-expanded={delayExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Delay</h3>
              <p css={collapsibleSubtitle}>Configure the delay between requests</p>
            </div>
            <ChevronDownIcon
              style={{
                transform: delayExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {delayExpanded && (
            <div css={collapsibleBody}>
              <p css={fieldHelpText}>
                Add a delay (ms) between requests to simulate a real user.
              </p>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="delay-ms-input">
                  Delay (ms)
                </label>
                <input
                  id="delay-ms-input"
                  type="number"
                  min="0"
                  css={textInput()}
                  style={{ maxWidth: "200px" }}
                  placeholder="0"
                  value={values.delayMs === 0 ? "0" : values.delayMs}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    onChange({ delayMs: isNaN(parsed) ? 0 : parsed });
                  }}
                />
                <p css={fieldHelpText}>Delay in milliseconds (default: 0)</p>
              </div>
            </div>
          )}
        </div>

        {/* Field 5: Extension Hook (Collapsible) */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(extensionExpanded)}
            onClick={() => setExtensionExpanded(!extensionExpanded)}
            aria-expanded={extensionExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Extension Hook</h3>
              <p css={collapsibleSubtitle}>
                Run custom code at specific points in the evaluation lifecycle
              </p>
            </div>
            <ChevronDownIcon
              style={{
                transform: extensionExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {extensionExpanded && (
            <div css={collapsibleBody}>
              <p css={fieldHelpText}>
                Path to custom extension hook file (JavaScript or Python). Format:{" "}
                <code>/path/to/file.py:hookFunction</code> or <code>/path/to/file.js:hookFunction</code>.
              </p>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="extension-hook-input">
                  Extension Hook Path
                </label>
                <input
                  id="extension-hook-input"
                  type="text"
                  css={textInput()}
                  placeholder="/path/to/hook.py:my_hook"
                  value={values.extensionHook}
                  onChange={(e) => onChange({ extensionHook: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Test Connection Section */}
        {onTestConnection && (
          <div css={testConnectionSection}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Button
                variant="secondary"
                size="medium"
                onClick={onTestConnection}
                disabled={isTestingConnection || !values.filePath.trim()}
                style={{
                  background: "#0f766e",
                  color: "#ffffff",
                  border: "none",
                  fontWeight: 650,
                  cursor: isTestingConnection || !values.filePath.trim() ? "not-allowed" : "pointer"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <WifiIcon />
                  <span>{isTestingConnection ? "Testing Agent Process..." : "Test Target Connection"}</span>
                </div>
              </Button>

              {testResult && (
                <div>
                  {testResult.success ? (
                    <span css={testResultSuccessPill}>
                      <CheckIcon />
                      <span>
                        Connected {testResult.response_time || testResult.latency ? `(${testResult.response_time || testResult.latency})` : ""}
                      </span>
                    </span>
                  ) : (
                    <span css={testResultErrorPill}>
                      <CrossIcon />
                      <span>{testResult.message || testResult.error_message || "Connection failed"}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <p css={fieldHelpText} style={{ margin: "2px 0 0 0" }}>
              Invokes the configured Python agent with a harmless diagnostic sample prompt and verifies the real response.
            </p>
            {testResult && testResult.success && (testResult.sample_response || testResult.sample_output || testResult.output) && (
              <div style={{
                marginTop: "10px",
                padding: "12px",
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: "8px"
              }}>
                <div style={{ fontSize: "0.80rem", fontWeight: 750, color: "#0f766e", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Real Agent Response (Live Verified):
                </div>
                <div style={{
                  fontSize: "0.85rem",
                  color: "#0f172a",
                  whiteSpace: "pre-wrap",
                  maxHeight: "180px",
                  overflowY: "auto",
                  fontFamily: "monospace",
                  lineHeight: "1.4"
                }}>
                  {testResult.sample_response || testResult.sample_output || testResult.output}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Field 6: Prompts */}
        <div css={promptsSection}>
          <h2 css={promptsHeader}>Prompts</h2>
          <p css={fieldHelpText}>
            Enter your prompts below. Use{" "}
            <code
              style={{
                background: "#f1f5f9",
                padding: "2px 6px",
                borderRadius: "4px",
                fontFamily: "monospace",
                color: "#0f766e",
                fontWeight: 650,
              }}
            >
              {"{{ prompt }}"}
            </code>{" "}
            as a placeholder where you want the user's input to appear in your prompt template.
          </p>

          {values.prompts.map((promptText, index) => (
            <div key={index} css={formGroup}>
              <label css={fieldLabel} htmlFor={`prompt-input-${index}`}>
                {values.prompts.length === 1 ? "Prompt" : `Prompt ${index + 1}`}
              </label>
              <div css={promptItemRow}>
                <textarea
                  id={`prompt-input-${index}`}
                  css={promptTextarea}
                  rows={3}
                  placeholder="{{prompt}}"
                  value={promptText}
                  onChange={(e) => handlePromptChange(index, e.target.value)}
                />
                <button
                  type="button"
                  css={trashButton}
                  onClick={() => handleRemovePrompt(index)}
                  title={`Remove prompt ${index + 1}`}
                  aria-label={`Remove prompt ${index + 1}`}
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))}

          <button type="button" css={addPromptButton} onClick={handleAddPrompt}>
            <PlusIcon />
            <span>Add Prompt</span>
          </button>
        </div>
      </div>

      {/* 3. Bottom Navigation Bar */}
      {!hideBottomBar && (
        <div css={bottomNavRow}>
          <Button
            variant="default"
            type="button"
            style={{
              padding: "9px 22px",
              fontSize: "0.92rem",
              fontWeight: 650,
              borderRadius: "8px",
              border: "1.5px solid #cbd5e1",
              color: "#475569",
              background: "#ffffff",
            }}
            onClick={onBack}
          >
            &lt; Back
          </Button>
          <Button
            type="button"
            style={{
              padding: "9px 24px",
              fontSize: "0.95rem",
              fontWeight: 750,
              borderRadius: "8px",
              background: "#0f766e",
              color: "#ffffff",
              border: "1px solid #115e59",
              cursor: "pointer",
            }}
            onClick={handleNextClick}
          >
            Next &gt;
          </Button>
        </div>
      )}
      {/* Template File Modal */}
      {isPython && (
        <PythonAgentTemplateModal
          isOpen={templateModalOpen}
          frameworkId={framework.id}
          onClose={() => setTemplateModalOpen(false)}
          onUseTemplatePath={(path) => onChange({ filePath: path })}
        />
      )}
    </div>
  );
}

// Backward-compatible Python Target Configuration
export function PythonTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType">
) {
  return <ScriptTargetConfiguration providerType="python" {...props} />;
}

// Framework Target Configuration Wrappers
export function LlamaIndexTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="llamaindex" {...props} />;
}

export function LangGraphTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="langgraph" {...props} />;
}

export function AutoGenTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="autogen" {...props} />;
}

export function CrewAITargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="crewai" {...props} />;
}

export function OpenAIAgentsTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="openai-agents" {...props} />;
}

export function GoogleADKTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType" | "frameworkId">
) {
  return <ScriptTargetConfiguration providerType="python" frameworkId="google-adk" {...props} />;
}

// JavaScript / TypeScript Target Configuration
export function JavaScriptTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType">
) {
  return <ScriptTargetConfiguration providerType="javascript" {...props} />;
}

// Go Target Configuration
export function GoTargetConfiguration(
  props: Omit<ScriptTargetConfigurationProps, "providerType">
) {
  return <ScriptTargetConfiguration providerType="go" {...props} />;
}
